import { useState, useRef } from 'react';
import { MdOutlineCancel } from 'react-icons/md';
import { ArrowDownTrayIcon, EyeIcon } from '@heroicons/react/24/solid';
import SeleccionaFecha from '../DatePicker/SeleccionaFecha';
import { useStateContext } from '../../contexts/ContextProvider';
import { updateAcreditacion } from '../../services/Acreditacion';
import { uploadFile, downloadFile, fetchFileUrl } from '../../services/filesTools';
import SearchDocMaquina from '../Search/SearchDocMaquina';
import { fetchUser } from '../../utils/fetchUser';
import { toast, Toaster } from 'react-hot-toast';
import { getMensajeRol } from '../../services/UsuarioSucursal';
import { crearMensaje, notificarUsuario } from '../../services/Util';

const esPdf = (nombre) => nombre?.toLowerCase().endsWith('.pdf');

function diasDiferencia(fechaStr) {
  if (!fechaStr) return { texto: '', dias: 0, vencida: false };
  let fecha;
  if (fechaStr instanceof Date) {
    fecha = new Date(fechaStr);
  } else {
    // Normalizar ISO datetime quitando la parte de hora
    const s = typeof fechaStr === 'string' && fechaStr.includes('T') ? fechaStr.split('T')[0] : fechaStr;
    if (typeof s === 'string' && s.includes('/')) {
      const [d, m, y] = s.split('/'); fecha = new Date(y, m - 1, d);
    } else if (typeof s === 'string' && s.includes('-')) {
      const [y, m, d] = s.split('-'); fecha = new Date(Number(y), Number(m) - 1, Number(d));
    } else {
      fecha = new Date(s);
    }
  }
  if (isNaN(fecha)) return { texto: '', dias: 0, vencida: false };
  const hoy = new Date(); hoy.setHours(0,0,0,0); fecha.setHours(0,0,0,0);
  const diffDias = Math.round((fecha - hoy) / 86400000);
  const dd = String(fecha.getDate()).padStart(2,'0');
  const mm = String(fecha.getMonth()+1).padStart(2,'0');
  const texto = `${dd}/${mm}/${fecha.getFullYear()} (${diffDias >= 0 ? diffDias : `-${Math.abs(diffDias)}`}) días`;
  return { texto, dias: diffDias, vencida: diffDias < 0 };
}

const MOTIVOS = [
  'Operador',
  'No se Presenta',
  'Condición Climática',
  'Mecánica',
  'Implementación / GPS',
  'Estándar',
  'Documental',
  'Otro',
];

// ── Sección técnica ────────────────────────────────────────────────────────────
const ItemTecnico = ({ label, value, onChange, onDescargar, onPrevisualizar }) => {
  const fileRef = useRef(null);

  const handleEstado = (estado) =>
    onChange({ ...value, estado, motivos: estado === 'Rechazado' ? value.motivos : [], comentario: estado === 'Rechazado' ? value.comentario : '' });

  const toggleMotivo = (m) => {
    const motivos = value.motivos.includes(m) ? value.motivos.filter(x => x !== m) : [...value.motivos, m];
    onChange({ ...value, motivos });
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const response = await uploadFile(file);
      if (response?.fileId) onChange({ ...value, idArchivo: response.fileId, nombreArchivo: file.name });
    } catch (err) {
      console.error('Error subiendo archivo:', err);
    } finally {
      e.target.value = '';
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 mb-3 bg-white">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex gap-2">
          {['Aprobado', 'Rechazado'].map(op => (
            <button key={op} type="button" onClick={() => handleEstado(op)}
              className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${
                value.estado === op
                  ? op === 'Aprobado' ? 'bg-green-500 text-white border-green-500' : 'bg-red-500 text-white border-red-500'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}>
              {op}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 ml-auto flex-wrap">
          <input type="file" accept=".pdf,.xlsx,.xls,.zip,.rar,.docx,.doc,.png,.jpg,.jpeg" ref={fileRef} onChange={handleUpload} className="hidden" />
          <button type="button" onClick={() => fileRef.current?.click()}
            className="text-xs px-2.5 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">
            {value.nombreArchivo ? 'Reemplazar' : 'Subir archivo'}
          </button>
          {value.nombreArchivo && (
            <span className="text-xs text-gray-500 truncate max-w-[140px]" title={value.nombreArchivo}>
              {value.nombreArchivo}
            </span>
          )}
          {value.idArchivo && (
            <>
              <button type="button" title="Vista previa" onClick={() => onPrevisualizar(value)}
                className="p-1 bg-indigo-500 text-white rounded hover:bg-indigo-600 disabled:opacity-40"
                disabled={!esPdf(value.nombreArchivo)}>
                <EyeIcon className="w-4 h-4" />
              </button>
              <button type="button" title="Descargar" onClick={() => onDescargar(value)}
                className="p-1 bg-green-500 text-white rounded hover:bg-green-600">
                <ArrowDownTrayIcon className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {value.estado === 'Rechazado' && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Motivos</label>
            <div className="flex flex-wrap gap-2">
              {MOTIVOS.map(m => (
                <button key={m} type="button" onClick={() => toggleMotivo(m)}
                  className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${
                    value.motivos.includes(m)
                      ? 'bg-red-100 text-red-700 border-red-300'
                      : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                  }`}>
                  {m}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Comentario</label>
            <textarea rows={2}
              className="w-full text-xs border border-gray-300 rounded p-2 resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
              value={value.comentario}
              onChange={e => onChange({ ...value, comentario: e.target.value })} />
          </div>
        </div>
      )}
    </div>
  );
};

// ── Tabla de documentos Obra — idéntica a la Carpeta ─────────────────────────
const TablaDocsObra = ({ docs, onChange, onDescargar, onPrevisualizar }) => {
  const fileRefs = useRef({});

  const handleUpload = async (index, e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const response = await uploadFile(file);
      if (response?.fileId) {
        onChange(index, { ...docs[index], idArchivo: response.fileId, nombreArchivo: file.name, urlArchivo: URL.createObjectURL(file) });
      }
    } catch (err) {
      console.error('Error subiendo archivo:', err);
    } finally {
      e.target.value = '';
    }
  };

  if (docs.length === 0) return <p className="text-xs text-gray-400 italic mt-1">Sin documentos de obra asignados.</p>;

  return (
    <table className="w-full border-collapse border border-gray-300 text-sm mt-2">
      <thead>
        <tr className="bg-gray-200 text-left">
          <th className="text-xs font-semibold p-2 w-2/6 border">Documento</th>
          <th className="text-xs font-semibold p-2 w-1/6 border">Fecha Emisión</th>
          <th className="text-xs font-semibold p-2 w-1/6 border">Vigencia</th>
          <th className="text-xs font-semibold p-2 w-1/6 border">Fecha Vencimiento</th>
          <th className="text-xs font-semibold p-2 w-1/6 border">Archivo</th>
          <th className="text-xs font-semibold p-2 w-1/6 border">Acciones</th>
        </tr>
      </thead>
      <tbody>
        {docs.map((doc, index) => (
          <tr key={index} className="border border-gray-300">
            <td className="border p-2 bg-gray-100 font-semibold text-xs">{doc.documentoGeneral}</td>

            <td className="border p-2">
              <SeleccionaFecha
                selectedDate={doc.fechaVigencia ? new Date(doc.fechaVigencia) : null}
                handleDateChange={date => onChange(index, { ...doc, fechaVigencia: date })}
              />
            </td>

            <td className={`border p-2 text-xs font-semibold ${diasDiferencia(doc.fechaVencimiento).vencida ? 'bg-red-300 text-red-900' : 'bg-gray-100'}`}>
              {diasDiferencia(doc.fechaVencimiento).texto || '—'}
            </td>

            <td className="border p-2">
              <SeleccionaFecha
                selectedDate={doc.fechaVencimiento ? new Date(doc.fechaVencimiento) : null}
                handleDateChange={date => onChange(index, { ...doc, fechaVencimiento: date })}
              />
            </td>

            <td className="border p-2">
              <div className="flex flex-col">
                <input
                  type="file"
                  accept=".pdf,.xlsx,.xls,.zip,.rar,.docx,.doc,.png,.jpg,.jpeg"
                  ref={el => fileRefs.current[index] = el}
                  onChange={e => handleUpload(index, e)}
                  className="mt-2"
                />
                {doc.nombreArchivo && (
                  <p className="text-xs text-gray-600 mt-1">Archivo: {doc.nombreArchivo}</p>
                )}
              </div>
            </td>

            <td className="border p-2">
              <div className="flex gap-2">
                <button type="button"
                  className="p-1 bg-indigo-500 text-white rounded hover:bg-indigo-600 disabled:opacity-40"
                  onClick={() => onPrevisualizar(doc)}
                  disabled={!doc.idArchivo || !esPdf(doc.nombreArchivo)}
                  title="Vista previa">
                  <EyeIcon className="w-4 h-4" />
                </button>
                <button type="button"
                  className="p-1 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-40"
                  onClick={() => onDescargar(doc)}
                  disabled={!doc.idArchivo}
                  title="Descargar">
                  <ArrowDownTrayIcon className="w-4 h-4" />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ── Fila documento Logística (aprobar/rechazar + comentario + preview/download) ──
const FilaDocumentoLogistica = ({ doc, onChange, onDescargar, onPrevisualizar }) => {
  const handleEstado = (estado) => onChange({ ...doc, estado, comentario: estado === 'Rechazado' ? doc.comentario : '' });

  return (
    <div className="border border-gray-200 rounded-lg p-3 mb-2 bg-white">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-gray-700 flex-1 min-w-0 truncate">{doc.documentoGeneral}</span>
        <div className="flex gap-1.5">
          {['Aprobado', 'Rechazado'].map(op => (
            <button key={op} type="button" onClick={() => handleEstado(op)}
              className={`text-xs px-3 py-1 rounded-full border font-medium transition-colors ${
                doc.estado === op
                  ? op === 'Aprobado' ? 'bg-green-500 text-white border-green-500' : 'bg-red-500 text-white border-red-500'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}>
              {op}
            </button>
          ))}
        </div>
        {doc.idArchivo && (
          <div className="flex gap-1">
            <button type="button" title="Vista previa" onClick={() => onPrevisualizar(doc)}
              className="p-1 bg-indigo-500 text-white rounded hover:bg-indigo-600 disabled:opacity-40"
              disabled={!esPdf(doc.nombreArchivo)}>
              <EyeIcon className="w-4 h-4" />
            </button>
            <button type="button" title="Descargar" onClick={() => onDescargar(doc)}
              className="p-1 bg-green-500 text-white rounded hover:bg-green-600">
              <ArrowDownTrayIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      {doc.estado === 'Rechazado' && (
        <div className="mt-2">
          <textarea rows={2} placeholder="Comentario de rechazo..."
            className="w-full text-xs border border-gray-300 rounded p-2 resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
            value={doc.comentario || ''}
            onChange={e => onChange({ ...doc, comentario: e.target.value })} />
        </div>
      )}
    </div>
  );
};

// ── Componente principal ───────────────────────────────────────────────────────
const DetalleAcreditacion = ({ data, onChangeDatos }) => {
  const { isClicked, setIsClicked } = useStateContext();
  const userInfo = fetchUser();

  const [tecnica, setTecnica] = useState({
    checklist: {
      estado:        data.tecnica?.checklist?.estado        || 'Pendiente',
      motivos:       data.tecnica?.checklist?.motivos       || [],
      comentario:    data.tecnica?.checklist?.comentario    || '',
      nombreArchivo: data.tecnica?.checklist?.nombreArchivo || null,
      idArchivo:     data.tecnica?.checklist?.idArchivo     || null,
    },
  });

  const [docsObra,      setDocsObra]      = useState((data.documentosObra      || []).map(d => ({ ...d })));
  const [docsLogistica, setDocsLogistica] = useState((data.documentosLogistica || []).map(d => ({ ...d })));
  const [fechaAcreditacion, setFechaAcreditacion] = useState(data.fechaAcreditacion ? new Date(data.fechaAcreditacion) : null);
  const [fechaPresentacion, setFechaPresentacion] = useState(data.fechaPresentacion ? new Date(data.fechaPresentacion) : null);
  const [preview,   setPreview]   = useState({ open: false, url: null });
  const [guardando, setGuardando] = useState(false);
  const [erroresValidacion, setErroresValidacion] = useState([]);

  const inputClass = "text-[11px] border border-gray-300 p-1.5 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400";
  const centro     = data.idCentro;
  const idMandante = data.idMandante?._id || data.idMandante;
  const idMaquina  = data.idMaquina?._id  || data.idMaquina;

  const cerrar = () => {
    if (preview.url) window.URL.revokeObjectURL(preview.url);
    setIsClicked({ ...isClicked, detalleAcreditacion: false });
  };

  const descargar = async (doc) => {
    if (doc.idArchivo) await downloadFile(doc.idArchivo, doc.nombreArchivo || 'documento.pdf');
  };

  const previsualizar = async (doc) => {
    if (!doc.idArchivo) return;
    const url = await fetchFileUrl(doc.idArchivo);
    setPreview({ open: true, url });
  };

  const handleAgregarDocObra = (doc) => {
    const yaExiste = docsObra.some(d => String(d.idDocuGeneral) === String(doc.idDocuGeneral));
    if (yaExiste) return;
    setDocsObra(prev => [...prev, {
      idDocuGeneral:    doc.idDocuGeneral,
      documentoGeneral: doc.documentoGeneral,
      nombreArchivo:    null,
      idArchivo:        null,
      fechaVigencia:    null,
      fechaVencimiento: null,
    }]);
  };

  const validar = () => {
    const errores = [];

    // Misma lógica que updateAcreditacion (backend) para saber si el guardado
    // resultará en 'Aprobada': solo en ese caso la fecha es obligatoria.
    const todosItems = [tecnica.checklist, ...docsLogistica];
    const hayRechazado = todosItems.some(i => i.estado === 'Rechazado');
    const todoAprobado = todosItems.every(i => i.estado === 'Aprobado');
    const seVaAprobar = !hayRechazado && todoAprobado;

    if (seVaAprobar && !fechaAcreditacion) errores.push('Debe ingresar la fecha de acreditación.');

    const itemsTecnica = [
      { label: 'Checklist', item: tecnica.checklist },
    ];

    for (const { label, item } of itemsTecnica) {
      if (item.estado === 'Pendiente') {
        errores.push(`Técnica — ${label}: debe marcarse como Aprobado o Rechazado.`);
      } else if (item.estado === 'Rechazado') {
        if (!item.motivos?.length)    errores.push(`Técnica — ${label}: seleccione al menos un motivo de rechazo.`);
        if (!item.comentario?.trim()) errores.push(`Técnica — ${label}: ingrese un comentario de rechazo.`);
      }
      if (!item.idArchivo) errores.push(`Técnica — ${label}: debe cargar un archivo.`);
    }

    for (const doc of docsObra) {
      if (!doc.idArchivo)       errores.push(`Obra — "${doc.documentoGeneral}": debe cargar un archivo.`);
      if (!doc.fechaVigencia)   errores.push(`Obra — "${doc.documentoGeneral}": debe ingresar fecha de emisión.`);
      if (!doc.fechaVencimiento) errores.push(`Obra — "${doc.documentoGeneral}": debe ingresar fecha de vencimiento.`);
    }

    for (const doc of docsLogistica) {
      if (doc.estado === 'Pendiente') {
        errores.push(`Logística — "${doc.documentoGeneral}": debe marcarse como Aprobado o Rechazado.`);
      } else if (doc.estado === 'Rechazado' && !doc.comentario?.trim()) {
        errores.push(`Logística — "${doc.documentoGeneral}": ingrese un comentario de rechazo.`);
      }
    }

    return errores;
  };

  const guardar = async () => {
    const errores = validar();
    if (errores.length > 0) {
      setErroresValidacion(errores);
      return;
    }
    setErroresValidacion([]);

    setGuardando(true);
    try {
      const resultado = await updateAcreditacion(data._id, {
        tecnica,
        documentosObra:      docsObra,
        documentosLogistica: docsLogistica,
        fechaAcreditacion,
        fechaPresentacion,
        idUsuario:    userInfo.usuario,
        nombreUsuario: userInfo.nombre || '',
      });

      const nuevoEstado = resultado?.estado;
      if (nuevoEstado !== data.estado && (nuevoEstado === 'Aprobada' || nuevoEstado === 'Rechazada')) {
        const templateKey = nuevoEstado === 'Aprobada' ? 'acreditacionAprobada' : 'acreditacionRechazada';
        const dataMensaje = {
          numeroCarpeta: data.numeroCarpeta,
          codigoEquipo:  data.codigoEquipo,
          serie:         data.serie,
          patente:       data.patente,
          nombreProyecto: centro?.nombreProyecto || '',
          codigoCentro:   centro?.codigo         || '',
        };
        try {
          const usuarios = await getMensajeRol('Logística');
          if (usuarios?.length) {
            await Promise.allSettled(
              usuarios.map(u => notificarUsuario(JSON.stringify(crearMensaje(u._id, templateKey, dataMensaje))))
            );
          }
        } catch (e) {
          console.error('Error enviando notificaciones:', e);
        }
      }

      toast.success('Cambios guardados correctamente');
      onChangeDatos('REFRESH');
      cerrar();
    } catch (err) {
      console.error('Error guardando acreditación:', err);
      toast.error('Error al guardar los cambios');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="bg-half-transparent w-full fixed nav-item top-0 right-0 z-50">
      <Toaster />
      <div className="overflow-y-scroll float-right h-screen duration-1000 ease-in-out dark:text-gray-200 transition-all dark:bg-[#484B52] bg-gray-100 w-full md:w-full p-8">

        {/* Cabecera */}
        <div className="flex justify-between items-center px-4 py-2 border-b border-gray-200 bg-white rounded-t-md shadow-sm">
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
            <p className="text-gray-800 font-semibold text-base md:text-lg">Acreditación</p>
          </div>
          <button type="button" onClick={cerrar}
            className="text-gray-400 hover:text-red-500 transition-colors duration-150 p-2 rounded-full hover:bg-gray-100">
            <MdOutlineCancel className="text-xl md:text-2xl" />
          </button>
        </div>

        {/* Info del equipo */}
        <div className="mt-5 space-y-4">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <div className="grid grid-cols-2 md:grid-cols-8 gap-4">
              <div className="col-span-1 flex flex-col">
                <label className="text-xs font-semibold mb-1">N° Carpeta</label>
                <input type="text" className={inputClass} value={data.numeroCarpeta || ''} disabled />
              </div>
              <div className="col-span-1 flex flex-col">
                <label className="text-xs font-semibold mb-1">Estado</label>
                <input type="text" className={inputClass} value={data.estado || ''} disabled />
              </div>
              <div className="col-span-1 flex flex-col">
                <label className="text-xs font-semibold mb-1">Fecha Acreditación</label>
                <SeleccionaFecha
                  selectedDate={fechaAcreditacion}
                  handleDateChange={date => setFechaAcreditacion(date)}
                />
              </div>
              <div className="col-span-1 flex flex-col">
                <label className="text-xs font-semibold mb-1">Fecha Presentación</label>
                <SeleccionaFecha
                  selectedDate={fechaPresentacion}
                  handleDateChange={date => setFechaPresentacion(date)}
                />
              </div>
              <div className="col-span-2 flex flex-col">
                <label className="text-xs font-semibold mb-1">Proyecto</label>
                <input type="text" className={inputClass} value={centro?.nombreProyecto || ''} disabled />
              </div>
              <div className="col-span-1 flex flex-col">
                <label className="text-xs font-semibold mb-1">C.Costo</label>
                <input type="text" className={inputClass} value={centro?.codigo || ''} disabled />
              </div>
              <div className="col-span-1 flex flex-col">
                <label className="text-xs font-semibold mb-1">N° Interno</label>
                <input type="text" className={inputClass} value={data.codigoEquipo || ''} disabled />
              </div>
              <div className="col-span-1 flex flex-col">
                <label className="text-xs font-semibold mb-1">Máquina</label>
                <input type="text" className={inputClass} value={data.idMaquina?.nombreMaquina || ''} disabled />
              </div>
              <div className="col-span-1 flex flex-col">
                <label className="text-xs font-semibold mb-1">Serie</label>
                <input type="text" className={inputClass} value={data.serie || ''} disabled />
              </div>
              <div className="col-span-1 flex flex-col">
                <label className="text-xs font-semibold mb-1">Patente</label>
                <input type="text" className={inputClass} value={data.patente || ''} disabled />
              </div>
            </div>
          </div>
        </div>

        {/* Banner Salida de Obra */}
        {data.estado === 'Salida de Obra' && (
          <div className="mt-4 bg-gray-100 border border-gray-300 rounded-lg p-4 flex items-center gap-3">
            <span className="text-gray-500 text-lg">&#9432;</span>
            <p className="text-sm text-gray-600">
              Este equipo realizó un <strong>traspaso de CC</strong>. La acreditación quedó cerrada junto con la carpeta y no puede editarse.
            </p>
          </div>
        )}

        {/* Sección Técnica */}
        <div className="bg-white rounded-lg shadow-sm p-4 mt-2">
          <p className="text-sm font-bold text-gray-700 mb-3 border-b pb-2">Acreditación Técnica</p>
          <ItemTecnico label="Checklist"
            value={tecnica.checklist}
            onChange={v => setTecnica(t => ({ ...t, checklist: v }))}
            onDescargar={descargar}
            onPrevisualizar={previsualizar} />
        </div>

        {/* Sección Documental */}
        <div className="bg-white rounded-lg shadow-sm p-4 mt-2">
          <p className="text-sm font-bold text-gray-700 mb-3 border-b pb-2">Acreditación Documental</p>

          {/* Documentos Obra */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Documentos de Obra</p>
              <SearchDocMaquina proyecto={idMandante} maquina={idMaquina} soloObra={true}
                onValorSeleccionadoChange={handleAgregarDocObra} />
            </div>
            <TablaDocsObra
              docs={docsObra}
              onChange={(i, v) => setDocsObra(prev => prev.map((d, j) => j === i ? v : d))}
              onDescargar={descargar}
              onPrevisualizar={previsualizar}
            />
          </div>

          {/* Separador */}
          <div className="border-t border-gray-200 my-6" />

          {/* Documentos Logística */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Documentos Logística (Carpeta)</p>
              {docsLogistica.length > 0 && (() => {
                const estadoDocumental =
                  docsLogistica.every(d => d.estado === 'Aprobado') ? 'Aprobado'
                  : docsLogistica.some(d => d.estado === 'Rechazado') ? 'Rechazado'
                  : null;
                return (
                  <div className="flex gap-2">
                    {['Aprobado', 'Rechazado'].map(op => (
                      <button key={op} type="button"
                        onClick={() => setDocsLogistica(prev => prev.map(d => ({ ...d, estado: op })))}
                        className={`text-xs px-3 py-1 rounded-full border font-medium transition-colors ${
                          estadoDocumental === op
                            ? op === 'Aprobado' ? 'bg-green-500 text-white border-green-500' : 'bg-red-500 text-white border-red-500'
                            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                        }`}>
                        {op}
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
            {docsLogistica.length === 0
              ? <p className="text-xs text-gray-400 italic">Sin documentos logísticos.</p>
              : docsLogistica.map((doc, i) => (
                  <FilaDocumentoLogistica key={i} doc={doc}
                    onChange={v => setDocsLogistica(prev => prev.map((d, j) => j === i ? v : d))}
                    onDescargar={descargar}
                    onPrevisualizar={previsualizar} />
                ))
            }
          </div>
        </div>

        {/* Errores de validación */}
        {erroresValidacion.length > 0 && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm font-semibold text-red-700 mb-2">Completa los siguientes campos antes de guardar:</p>
            <ul className="space-y-1">
              {erroresValidacion.map((msg, i) => (
                <li key={i} className="text-xs text-red-600 flex items-start gap-1.5">
                  <span className="mt-0.5 shrink-0">•</span>
                  <span>{msg}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Botones */}
        <div className="flex justify-end gap-3 mt-4">
          <button type="button" onClick={cerrar}
            className="text-sm px-5 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition">
            Cerrar
          </button>
          <button type="button" onClick={guardar} disabled={guardando}
            className="text-sm px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
            {guardando ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </div>

      {/* Preview modal */}
      {preview.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[85vh] flex flex-col">
            <div className="flex justify-between items-center p-3 border-b">
              <span className="text-sm font-semibold">Vista previa</span>
              <button type="button" onClick={() => { window.URL.revokeObjectURL(preview.url); setPreview({ open: false, url: null }); }}
                className="text-gray-400 hover:text-red-500">
                <MdOutlineCancel className="text-xl" />
              </button>
            </div>
            <iframe src={preview.url} className="flex-1 w-full" title="vista previa" />
          </div>
        </div>
      )}
    </div>
  );
};

export default DetalleAcreditacion;
